import React, { useMemo, useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import Select from "react-select";
import { TableRow, TableCell, Dialog, DialogContent, DialogActions, Divider } from "@mui/material";
import toast from "react-hot-toast";

import { ADD_GROUP_MEMBER } from "../../api/group";
import { UseUserId } from "../../hooks/auth";
import { getCompanyUsers, updateCompanyUser } from "../../redux/company/actions";
import SoftTypography from "../../components/SoftTypography";
import CloseIcon from "@mui/icons-material/Close";

// Soft UI Dashboard PRO React base styles
import RemovePlayer from "./RemovePlayer";
import ArchivePlayer from "./ArchivePlayer";
import BillingUpdate from "./BillingUpdate";
import UpdatePlayerRole from "./UpdatePlayerRole";
import EditPlayerDetails from "./EditPlayerDetails";
import SoftBox from "../../components/SoftBox";
import SoftButton from "../../components/SoftButton";

const escapeRegExp = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Highlights `query` inside `text` via JSX <mark> spans instead of mutating
 * live DOM text nodes (the table used to run mark.js directly against the
 * rendered rows on every search keystroke, which fought React's own
 * reconciliation of that subtree and could leave row event handlers — the
 * Actions kebab in particular — unresponsive after a search).
 */
const highlightMatch = (text, query) => {
  if (!text || !query) return text;
  const parts = String(text).split(new RegExp(`(${escapeRegExp(query)})`, "gi"));
  if (parts.length === 1) return text;
  let offset = 0;
  return parts.map((part) => {
    const key = `${offset}-${part}`;
    offset += part.length;
    return part.toLowerCase() === query.toLowerCase() ? (
      <mark key={key} style={{ background: "#FFF3B0", color: "inherit", borderRadius: 2, padding: "0 1px" }}>
        {part}
      </mark>
    ) : (
      part
    );
  });
};

function menuItemStyle(variant) {
  const isDanger = variant === "danger";
  return {
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
    padding: "8px 10px",
    border: 0,
    background: "transparent",
    borderRadius: 8,
    cursor: "pointer",
    fontFamily: "DM Sans",
    fontSize: 12.5,
    fontWeight: 500,
    color: isDanger ? "#B42318" : "#475467",
    textAlign: "left",
  };
}

/**
 * Compact kebab menu for row actions. Hides the inline ArchivePlayer +
 * RemovePlayer triggers (hideTrigger) and fires their dialogs via refs.
 */
function RowActionsMenu({ user, isLoggedInUser, isFirstAdmin, archiveUser, openBillingUpdateDialog }) {
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const archiveRef = useRef(null);
  const removeRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  const onEdit = () => { setOpen(false); setEditOpen(true); };
  const onArchive = () => { setOpen(false); archiveRef.current?.open(); };
  const onDelete = () => { setOpen(false); removeRef.current?.open(); };

  return (
    <div ref={menuRef} style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        onClick={() => !isFirstAdmin && setOpen((v) => !v)}
        aria-label="Row actions"
        disabled={isFirstAdmin}
        style={{
          width: 28,
          height: 28,
          padding: 0,
          border: 0,
          background: open ? "#F2F4F7" : "transparent",
          borderRadius: 8,
          color: "#98A2B3",
          cursor: isFirstAdmin ? "default" : "pointer",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="5" r="1" />
          <circle cx="12" cy="12" r="1" />
          <circle cx="12" cy="19" r="1" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: "absolute",
            right: 0,
            top: "calc(100% + 4px)",
            zIndex: 30,
            minWidth: 160,
            // Frosted glass — translucent white + blur (55% is the tuned value).
            background: "rgba(255,255,255,0.55)",
            backdropFilter: "blur(26px) saturate(185%)",
            WebkitBackdropFilter: "blur(26px) saturate(185%)",
            border: "1px solid rgba(255,255,255,0.55)",
            borderRadius: 8,
            padding: 6,
            boxShadow:
              "0 16px 40px -12px rgba(16,24,40,0.22), 0 2px 6px -2px rgba(16,24,40,0.10), inset 0 1px 0 rgba(255,255,255,0.7)",
            fontFamily: "DM Sans",
          }}
        >
          <button
            type="button"
            onClick={onEdit}
            style={menuItemStyle()}
            onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(15,23,42,0.05)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ color: "#98A2B3" }}>
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
            Edit details
          </button>
          <div style={{ height: 1, background: "rgba(15,23,42,0.06)", margin: "4px -6px" }} />
          <button
            type="button"
            onClick={onArchive}
            style={menuItemStyle()}
            onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(15,23,42,0.05)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ color: "#98A2B3" }}>
              <rect x="2" y="4" width="20" height="5" rx="2" /><path d="M4 9v9a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9" /><path d="M10 13h4" />
            </svg>
            {user.isArchived ? "Unarchive" : "Archive"}
          </button>
          <div style={{ height: 1, background: "rgba(15,23,42,0.06)", margin: "4px -6px" }} />
          <button
            type="button"
            onClick={onDelete}
            style={menuItemStyle("danger")}
            onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(229,72,77,0.10)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
            Delete
          </button>
        </div>
      )}

      {/* Edit details modal — opened from the "Edit details" item above */}
      <EditPlayerDetails
        open={editOpen}
        onClose={() => setEditOpen(false)}
        user={user}
      />

      {/* Dialogs rendered with hidden triggers — opened via refs */}
      <ArchivePlayer
        ref={archiveRef}
        isLoggedInUser={isLoggedInUser}
        archiveUser={archiveUser}
        user={user}
        archiveStatus={user.isArchived}
        openBillingUpdateDialog={openBillingUpdateDialog}
        isFirstAdmin={isFirstAdmin}
        hideTrigger
      />
      <RemovePlayer
        ref={removeRef}
        isLoggedInUser={isLoggedInUser}
        user={user}
        openBillingUpdateDialog={openBillingUpdateDialog}
        isFirstAdmin={isFirstAdmin}
        hideTrigger
      />
    </div>
  );
}

const User = ({
  user,
  workspaces,
  archiveUser,
  isFirstAdmin,
  visibleColumns,
  teams = [],
  assignUserToTeam,
  searchTerm = "",
}) => {
  // Helper: a column is visible when it's in the Set passed by the parent.
  // If no Set provided (legacy callers), default to showing everything.
  const showCol = (key) => !visibleColumns || visibleColumns.has(key);
  const authUserId = UseUserId();
  const isLoggedInUser = useMemo(
    () => authUserId === user?.userId,
    [authUserId, user._id],
  );
  const [billingUpdateDialog, setBillingUpdateDialog] = React.useState(false);
  const dispatch = useDispatch();

  // `workspaces` arrives from the parent already flattened (parent + labeled
  // sub-workspace options) -- it used to be rebuilt from scratch on every
  // single row's render, redoing the same reduce for every player in the
  // table any time it re-rendered.
  const currentWorkspaceOption = useMemo(
    () => workspaces.find((item) => item._id === user.groupId) || null,
    [workspaces, user.groupId],
  );

  const { mutateAsync: addGroupMember } = ADD_GROUP_MEMBER();
  // A single option object, not an array -- react-select's `value` for a
  // (non-multi) select is the selected option itself. The previous version
  // passed `workspaces.filter(...)` (an array) as the initial value, so the
  // "Move to" dropdown opened with nothing visibly selected even though the
  // player already had a workspace.
  const [selectedDepartment, setSelectedDepartment] = React.useState(currentWorkspaceOption);
  const [openWorkspaceModal, setOpenWorkspaceModal] = React.useState(false);
  const [updating, setUpdating] = React.useState(false);

  const openBillingUpdateDialog = () => setBillingUpdateDialog(true);
  const closeBillingUpdateDialog = () => setBillingUpdateDialog(false);

  const openWorkspacePicker = () => {
    setSelectedDepartment(currentWorkspaceOption);
    setOpenWorkspaceModal(true);
  };
  const closeWorkspaceModal = () => {
    if (updating) return; // don't let a backdrop click/Escape abandon an in-flight save
    setOpenWorkspaceModal(false);
  };

  const handleChange = (selectedOption) => {
    // If the option has a displayLabel (child workspace), use it instead of label
    if (selectedOption?.displayLabel) {
      selectedOption = {
        ...selectedOption,
        label: selectedOption.displayLabel
      };
    }
    setSelectedDepartment(selectedOption);
  };

  const handleUpdate = async () => {
    if (!selectedDepartment) return;
    // No-op save (picked the workspace the player is already in) -- just close.
    if (selectedDepartment.value === (user.groupId || "")) {
      setOpenWorkspaceModal(false);
      return;
    }
    setUpdating(true);
    // Optimistic: reflect the new workspace immediately across the table
    // (this row's button, the pill above) instead of waiting on the round
    // trip -- previously the only feedback was a full company-wide user
    // refetch after the request settled, with nothing shown while it ran.
    dispatch(updateCompanyUser(user.userId, { groupId: selectedDepartment.value }));
    try {
      await addGroupMember({
        groupId: selectedDepartment.value,
        data: { userId: user.userId },
      });
      dispatch(getCompanyUsers());
      toast.success(
        `Moved to ${selectedDepartment.displayLabel || selectedDepartment.label}`,
      );
      setOpenWorkspaceModal(false);
    } catch (err) {
      // Roll back the optimistic patch and the dropdown's own selection.
      dispatch(updateCompanyUser(user.userId, { groupId: user.groupId }));
      setSelectedDepartment(currentWorkspaceOption);
      toast.error(
        err?.response?.data?.message || "Couldn't move this player. Please try again.",
      );
    } finally {
      setUpdating(false);
    }
  };

  let status;

  if (user.isArchived) {
    status = "Archived";
  } else if (!user.groupId) {
    status = "No Workspace";
  } else if (user.logins > 0) {
    status = "Active";
  }else {
    status = "Pending";
  }

  const currentWsName =
    (currentWorkspaceOption?.displayLabel || currentWorkspaceOption?.label || "")
      .trim() || "—";

  return (
    <>
      {billingUpdateDialog && (
        <BillingUpdate
          billingUpdateDialog={billingUpdateDialog}
          closeBillingUpdateDialog={closeBillingUpdateDialog}
        />
      )}
      <Dialog
        open={openWorkspaceModal}
        onClose={closeWorkspaceModal}
        sx={{
          ".MuiPaper-root": {
            borderRadius: "8px",
            // Match the system's frosted-glass modal: translucent white over a
            // blurred backdrop, luminous border, soft shadow.
            background: "rgba(255, 255, 255, 0.72)",
            backdropFilter: "blur(18px) saturate(160%)",
            WebkitBackdropFilter: "blur(18px) saturate(160%)",
            boxShadow:
              "0px 24px 48px -8px rgba(16,24,40,0.18), inset 0px 1px 0px 0px rgba(255,255,255,0.7)",
            border: "1px solid rgba(255,255,255,0.6)",
            padding: 0,
            maxWidth: { mini: "92%", xs: "92%", sm: "440px" },
            width: "100%",
            overflow: "visible",
          },
        }}
      >
        {/* Header */}
        <SoftBox
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            padding: "20px 22px 8px",
          }}
        >
          <SoftTypography sx={{ fontSize: "17px", fontWeight: 700, color: "#101828" }}>
            Change workspace
          </SoftTypography>
          <SoftBox
            component="button"
            type="button"
            aria-label="Close"
            onClick={closeWorkspaceModal}
            disabled={updating}
            sx={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              border: 0,
              cursor: updating ? "default" : "pointer",
              opacity: updating ? 0.5 : 1,
              background: "#F2F4F7",
              color: "#667085",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              "& svg": { fontSize: "1rem !important" },
              "&:hover": { background: updating ? "#F2F4F7" : "#EAECF0" },
            }}
          >
            <CloseIcon />
          </SoftBox>
        </SoftBox>

        {/* Current workspace — small pill */}
        <SoftBox sx={{ padding: "0 22px" }}>
          <SoftBox
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              background: "#F2F4F7",
              borderRadius: "999px",
              padding: "4px 11px",
              fontFamily: "DM Sans",
              fontSize: "11.5px",
              color: "#475467",
            }}
          >
            <SoftBox component="span" sx={{ width: 5, height: 5, borderRadius: "50%", background: "#98A2B3" }} />
            Currently in&nbsp;
            <SoftBox component="span" sx={{ fontWeight: 600, color: "#344054", textTransform: "capitalize" }}>
              {currentWsName}
            </SoftBox>
          </SoftBox>
        </SoftBox>

        {/* Move to — full-width select */}
        <SoftBox sx={{ padding: "16px 22px 4px" }}>
          <SoftTypography sx={{ fontSize: "12px", fontWeight: 600, color: "#344054", mb: "6px" }}>
            Move to
          </SoftTypography>
          <Select
            options={workspaces}
            value={selectedDepartment}
            onChange={handleChange}
            isDisabled={updating}
            menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
            styles={{
              control: (base, state) => ({
                ...base,
                minHeight: "44px",
                borderRadius: "8px",
                background: "#fff",
                border: state.isFocused
                  ? "1.5px solid var(--gmb-primary, #1B598E)"
                  : "1px solid #D0D5DD",
                boxShadow: state.isFocused ? "0 0 0 3px rgba(27,89,142,0.08)" : "none",
                padding: "0 4px",
                textTransform: "capitalize",
                "&:hover": { borderColor: "#98A2B3" },
              }),
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              menu: (base) => ({
                ...base,
                borderRadius: "8px",
                overflow: "hidden",
                boxShadow: "0px 12px 24px -6px rgba(16,24,40,0.16)",
                textTransform: "capitalize",
              }),
              option: (base, state) => ({
                ...base,
                background: state.isFocused ? "rgba(15,23,42,0.06)" : "#fff",
                color: state.label?.includes("Sub-Workspaces") ? "#98A2B3" : "#1d2939",
                fontSize:
                  state.label?.includes("Sub-Workspaces") || state.label?.match(/^\s{4}/)
                    ? "14px"
                    : base.fontSize,
                textTransform: "capitalize",
                cursor: "pointer",
              }),
              singleValue: (base) => ({ ...base, color: "#1d2939 !important", textTransform: "capitalize" }),
              input: (base) => ({ ...base, color: "#1d2939 !important", textTransform: "capitalize" }),
            }}
          />
        </SoftBox>

        {/* Footer */}
        <SoftBox
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            padding: "20px 22px 20px",
          }}
        >
          <SoftButton
            onClick={closeWorkspaceModal}
            disabled={updating}
            sx={{
              background: "#fff",
              border: "1px solid #D0D5DD",
              borderRadius: "8px",
              color: "#344054",
              fontSize: "13px",
              fontWeight: 600,
              textTransform: "none",
              padding: "0 18px",
              height: "40px",
              boxShadow: "none",
              "&:hover": { background: "#F9FAFB" },
              "&.Mui-disabled": { opacity: 0.5, color: "#344054" },
            }}
          >
            Cancel
          </SoftButton>
          <SoftButton
            onClick={handleUpdate}
            disabled={updating || !selectedDepartment}
            sx={{
              background: "var(--gmb-primary, #1B598E)",
              border: 0,
              borderRadius: "8px",
              color: "#FFFFFF !important",
              fontSize: "13px",
              fontWeight: 600,
              textTransform: "none",
              padding: "0 20px",
              height: "40px",
              boxShadow: "none",
              minWidth: "88px",
              "&:hover": { background: "var(--gmb-primary, #1B598E)", filter: "brightness(0.96)" },
              "&.Mui-disabled": { background: "var(--gmb-primary, #1B598E)", color: "#FFFFFF !important", opacity: 0.6 },
            }}
          >
            {updating ? "Updating…" : "Update"}
          </SoftButton>
        </SoftBox>
      </Dialog>
      <TableRow>
        {showCol("Name") && (
        <TableCell>
          <SoftTypography
            sx={{ color: "#191D23", fontWeight: 500, fontSize: "18px" }}
          >
            {highlightMatch(`${user.firstName} ${user.lastName}`, searchTerm)}
          </SoftTypography>
        </TableCell>
        )}
        {showCol("Email") && (
        <TableCell>
          {/* Left-aligned, not centred: addresses vary a lot in length, so
              centring left every row starting at a different x and the column
              read as ragged. Flush left puts every address on one edge. */}
          <SoftBox display="flex" alignItems="center" justifyContent="flex-start">
            {user.email ? (
              <SoftTypography
                sx={{
                  fontFamily: "DM Sans",
                  fontSize: "13px",
                  fontWeight: 500,
                  color: "#191D23",
                  maxWidth: "220px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={user.email}
              >
                {highlightMatch(user.email, searchTerm)}
              </SoftTypography>
            ) : (
              <SoftTypography
                sx={{
                  fontFamily: "DM Sans",
                  fontSize: "12px",
                  fontStyle: "italic",
                  color: "#98A2B3",
                }}
              >
                Not set
              </SoftTypography>
            )}
          </SoftBox>
        </TableCell>
        )}
        {showCol("Workspace") && (
        <TableCell>
            <SoftBox display="flex" alignItems="center">
            <SoftButton
              size="small"
              color="primary"
              onClick={openWorkspacePicker}
              sx={{
                fontFamily: "DM Sans",
                width: "100%",
                maxWidth: "180px",
                height: "32px",
                borderRadius: "8px",
                border: "1px solid #E4E7EC",
                background: "#FFFFFF !important",
                color: "#475467",
                boxShadow: "none !important",
                opacity: "1 !important",
                textTransform: "none",
                fontSize: "12px",
                fontWeight: 500,
                padding: "0 12px",
                justifyContent: "flex-start",
                "&:hover": { background: "#F9FAFB !important", borderColor: "#D0D5DD", color: "#191D23" },
                "& svg": { color: "#98A2B3 !important", marginLeft: "0.25rem", fontSize: "12px !important" },
              }}
            >
              {(() => {
                if (!currentWorkspaceOption || !user.groupId) {
                  return "Select Workspace";
                }
                if (currentWorkspaceOption.parentId) {
                  const parentWorkspace = workspaces.find(w => w._id === currentWorkspaceOption.parentId);
                  return (
                    <>
                      {parentWorkspace?.name}{" "}
                      <span style={{fontSize: '12px', color: '#667085'}}>&nbsp;&gt;&nbsp; {currentWorkspaceOption.name}</span>
                    </>
                  );
                } else {
                  return currentWorkspaceOption.name;
                }
              })()}
            </SoftButton>
            </SoftBox>
        </TableCell>
        )}
        {showCol("Team") && (
        <TableCell>
          <SoftBox display="flex" alignItems="center" justifyContent="center">
            {(() => {
              // React-select (not a native <select>) so the menu is actually
              // styleable — frosted glass + brand-tinted selection, matching the
              // rest of the app's dropdowns. Native <select> renders its option
              // list with the OS chrome (the old harsh green), which can't be styled.
              const brand = "var(--gmb-primary, #1B598E)";
              const teamOptions = [
                { value: "", label: "— Unassigned" },
                ...teams.map((t) => ({ value: t, label: t })),
              ];
              const currentTeam =
                teamOptions.find((o) => o.value === (user.team || "")) || teamOptions[0];
              return (
                <Select
                  options={teamOptions}
                  value={currentTeam}
                  onChange={(opt) =>
                    assignUserToTeam && assignUserToTeam(user.userId, opt?.value || "")
                  }
                  isSearchable={false}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                  menuPlacement="auto"
                  components={{ IndicatorSeparator: null }}
                  styles={{
                    container: (base) => ({ ...base, minWidth: "150px" }),
                    control: (base, state) => ({
                      ...base,
                      minHeight: "36px",
                      height: "36px",
                      borderRadius: "8px",
                      background: "#FFFFFF",
                      border: state.isFocused
                        ? `1px solid ${brand}`
                        : "1px solid #EAECF0",
                      boxShadow: state.isFocused ? "0 0 0 3px rgba(27,89,142,0.08)" : "none",
                      fontFamily: "DM Sans",
                      fontSize: "13px",
                      fontWeight: 500,
                      cursor: "pointer",
                      "&:hover": { borderColor: state.isFocused ? brand : "#D2D6DA" },
                    }),
                    valueContainer: (base) => ({ ...base, padding: "0 8px" }),
                    singleValue: (base) => ({
                      ...base,
                      color: currentTeam.value ? "#191D23" : "#98A2B3",
                      fontWeight: 500,
                    }),
                    dropdownIndicator: (base) => ({ ...base, padding: "0 6px", color: "#98A2B3" }),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    menu: (base) => ({
                      ...base,
                      minWidth: "180px",
                      background: "rgba(255,255,255,0.55)",
                      backdropFilter: "blur(26px) saturate(185%)",
                      WebkitBackdropFilter: "blur(26px) saturate(185%)",
                      border: "1px solid rgba(255,255,255,0.55)",
                      borderRadius: "8px",
                      padding: "6px",
                      boxShadow:
                        "0 16px 40px -12px rgba(16,24,40,0.22), 0 2px 6px -2px rgba(16,24,40,0.10), inset 0 1px 0 rgba(255,255,255,0.7)",
                      overflow: "hidden",
                    }),
                    menuList: (base) => ({ ...base, padding: 0 }),
                    option: (base, state) => {
                      // Soft brand tint for the selected row — NOT the old green.
                      let optionBg = "transparent";
                      if (state.isSelected) optionBg = "rgba(27,89,142,0.10)";
                      else if (state.isFocused) optionBg = "rgba(15,23,42,0.05)";
                      return {
                        ...base,
                        fontFamily: "DM Sans",
                        fontSize: "13px",
                        fontWeight: state.isSelected ? 600 : 500,
                        borderRadius: "6px",
                        padding: "9px 10px",
                        cursor: "pointer",
                        background: optionBg,
                        color: state.isSelected ? brand : "#475467",
                        "&:active": { background: "rgba(27,89,142,0.16)" },
                      };
                    },
                  }}
                />
              );
            })()}
          </SoftBox>
        </TableCell>
        )}
        {showCol("Phone") && (
        <TableCell>
          <SoftBox display="flex" alignItems="center" justifyContent="center">
            {user.phoneNumber ? (
              <SoftTypography
                sx={{
                  fontFamily: "DM Sans",
                  fontSize: "13px",
                  fontWeight: 500,
                  color: "#191D23",
                }}
              >
                {user.phoneNumber}
              </SoftTypography>
            ) : (
              <SoftTypography
                sx={{
                  fontFamily: "DM Sans",
                  fontSize: "12px",
                  fontStyle: "italic",
                  color: "#98A2B3",
                }}
              >
                Not set
              </SoftTypography>
            )}
          </SoftBox>
        </TableCell>
        )}
        {showCol("Status") && (
        <TableCell>
          <SoftBox display="flex" flexDirection="column" alignItems="center" textAlign="center" justifyContent="center" sx={{ gap: "4px" }}>
            {(() => {
              // Color-code by status — green for live, amber for pending, gray for inactive.
              const styles = {
                Active:        { bg: "#ECFDF3", color: "#067647", dot: "#12B76A" },
                Pending:       { bg: "#FEF6EE", color: "#B93815", dot: "#F38744" },
                Archived:      { bg: "#F2F4F7", color: "#475467", dot: "#98A2B3" },
                "No Workspace":{ bg: "#F2F4F7", color: "#475467", dot: "#98A2B3" },
              };
              const s = styles[status] || styles.Archived;
              return (
                <SoftBox
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    background: s.bg,
                    color: s.color,
                    padding: "3px 10px",
                    fontFamily: "DM Sans",
                    fontSize: "12px",
                    fontWeight: 500,
                    borderRadius: "999px",
                  }}
                >
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.dot }} />
                  {status}
                </SoftBox>
              );
            })()}
            {status === "Pending" && (() => {
              // Whether the credentials email went out. "Sent" means Mailjet
              // accepted it, not that it reached the inbox.
              const mail = user.inviteEmail;
              const failed = mail?.status === "failed";
              let label = "No email on record";
              let title =
                "No credentials email is recorded for this player. They may have been added before tracking began, or the email never went out.";
              if (failed) {
                label = "Email failed";
                title = `The credentials email failed to send${mail.error ? `: ${mail.error}` : ""}`;
              } else if (mail) {
                const day = new Date(mail.at).toLocaleDateString(undefined, { month: "short", day: "numeric" });
                label = `Email sent ${day}`;
                title = "Mailjet accepted the credentials email. Ask the player to check spam if it hasn't arrived.";
              }
              return (
                <span
                  title={title}
                  style={{
                    fontFamily: "DM Sans",
                    fontSize: "11px",
                    color: failed ? "#B42318" : "#667085",
                    cursor: "help",
                  }}
                >
                  {label}
                </span>
              );
            })()}
          </SoftBox>
        </TableCell>
        )}
        {showCol("Permission") && (
        <TableCell>
          <SoftBox display="flex" alignItems="center" textAlign="center" justifyContent="center">
            <UpdatePlayerRole user={user} isLoggedInUser={isLoggedInUser} isFirstAdmin={isFirstAdmin} />
          {/* <Tooltip
            disableFocusListener
            title={`Click to set as ${user.isAdmin ? "Player" : "Admin"}`}
            placement="top"
          >
            <SoftButton
              size="small"
              variant={user.isAdmin ? "gradient" : "outlined"}
              color="primary"
              disabled={isLoggedInUser}
              sx={{ width: "8rem", borderRadius: "4px" }}
              onClick={() => handleAdmin(user.userId, user.isAdmin)}
            >
              {user.isAdmin ? (
                <>
                  Admin <PsychologyIcon fontSize="small" />
                </>
              ) : (
                <>
                  Player <SportsEsportsIcon fontSize="small" />
                </>
              )}
            </SoftButton>
          </Tooltip> */}
          </SoftBox>
        </TableCell>
        )}
        {showCol("Actions") && (
        <TableCell>
          <SoftBox display="flex" alignItems="center" justifyContent="center">
            <RowActionsMenu
              user={user}
              isLoggedInUser={isLoggedInUser}
              isFirstAdmin={isFirstAdmin}
              archiveUser={archiveUser}
              openBillingUpdateDialog={openBillingUpdateDialog}
            />
          </SoftBox>
        </TableCell>
        )}
      </TableRow>
    </>
  );
};
export default User;
